import React, {Fragment, useEffect, useRef, useState} from 'react';

import gsap from 'gsap';
import Draggable from 'gsap/dist/Draggable';
import Inertia from 'gsap/dist/InertiaPlugin';


import {Crumb} from './Breadcrumbs.types';

import Arrow from '../../../../assets/svg/BreadcrumbsArrow.svg';

import s from './Breadcrumbs.module.scss';
import {useIntl} from 'gatsby-plugin-intl';
import {useWindowSize} from '../../../../hooks/use-window-size';

interface IBreadcrumbs {
  crumbs: Crumb[];
  areCrumbsCentered?: boolean;
}

export const Breadcrumbs = ({crumbs, areCrumbsCentered = false}: IBreadcrumbs) => {
  const {locale} = useIntl();

  const [isOverflowing, setIsOverflowing] = useState<boolean>(false);
  const refCrumbs = useRef<HTMLDivElement>(null);
  const refWrapper = useRef<HTMLDivElement>(null);
  const windowSize = useWindowSize();

  const MAX_CRUMB_LENGTH = 35;
  const CRUMBS_WRAPPER_MARGIN = 30;

  const formatCrumb = (str: string) => trim(str[0].toUpperCase() + str.slice(1));

  const trim = (str: string) => {
    if (str.length > MAX_CRUMB_LENGTH) {
      return str.substr(0, MAX_CRUMB_LENGTH) + '...';
    }
    return str;
  };

  useEffect(() => {
    if (refCrumbs.current && refWrapper.current && windowSize.width) {
      const overflow = (refCrumbs.current.clientWidth) - (refWrapper.current.clientWidth - 2 * CRUMBS_WRAPPER_MARGIN);
      if (overflow > 0) {
        setIsOverflowing(true);
        gsap.registerPlugin(Draggable, Inertia);

        const draggable = Draggable.create(refCrumbs.current, {
          type: 'x',
          bounds: {
            minX: -overflow,
            maxX: 0,
            minY: 0,
            maxY: 0,
          },
          inertia: true,
          zIndexBoost: false,
        });

        return () => {
          draggable[0].kill();
        };
      } else {
        gsap.set(refCrumbs.current, {transform: 'none'});
        setIsOverflowing(false);
      }
    }
  }, [windowSize]);

  return (
    <div
      className={`${s.breadcrumbs} ${areCrumbsCentered && !isOverflowing ? s.centered : ''} ${isOverflowing ? s.overflows : ''}`}
      ref={refWrapper}>
      <div
        className={`${s.breadcrumbs__wrapper} ${isOverflowing ? s.overflows : ''}`}
        ref={refCrumbs}>
        {crumbs.map((crumb, i) => {
          const isLink = crumb.path;

          if (isLink) {
            return (
              <Fragment key={crumb.path}>
                <a className={s.breadcrumbs__link} href={crumb.path} hrefLang={locale}
                   title={crumb.name}>{formatCrumb(crumb.name)}</a>
                {
                  i < crumbs.length - 1 &&
                  <div className={s.breadcrumbs__arrow}>
                    <Arrow className={s.breadcrumbs__arrowSvg}/>
                  </div>
                }
              </Fragment>
            )
          } else {
            return (
              <Fragment key={crumb.name}>
                <span className={s.breadcrumbs__link}>{formatCrumb(crumb.name)}</span>
                {
                  i < crumbs.length - 1 &&
                  <div className={s.breadcrumbs__arrow}>
                    <Arrow className={s.breadcrumbs__arrowSvg}/>
                  </div>
                }
              </Fragment>
            )
          }
        })}
      </div>
    </div>
  );
};
